import { Component, ElementRef, Input, ViewChild } from '@angular/core';
export interface BarSeries { label: string; color: string; values: number[]; }

/** Grouped bar chart (time-series) — inline SVG, no charting library.
    Rounded/gradient bars, dashed grid, and an interactive hover tooltip + column highlight. */
@Component({
  selector: 'payarc-bar-chart',
  templateUrl: './payarc-bar-chart.component.html',
  styleUrls: ['./payarc-bar-chart.component.css'],
  standalone: false
})
export class PayarcBarChartComponent {
  @Input() categories: string[] = [];
  @Input() series: BarSeries[] = [];
  @Input() prefix = '$';
  @Input() suffix = '';

  private static nextId = 0;
  /** Per-instance prefix for gradient ids, so multiple charts on one page don't collide. */
  readonly uid = `pabar-${PayarcBarChartComponent.nextId++}`;

  @ViewChild('wrap') wrap!: ElementRef<HTMLElement>;
  hoverIndex: number | null = null;
  tipX = 0; tipY = 0;

  readonly W = 840; readonly H = 320;
  readonly padL = 68; readonly padR = 18; readonly padT = 18; readonly padB = 42;
  get plotW() { return this.W - this.padL - this.padR; }
  get plotH() { return this.H - this.padT - this.padB; }

  get maxVal() {
    let m = 0;
    for (const s of this.series) { for (const v of (s.values || [])) { if (v > m) { m = v; } } }
    return m;
  }
  private niceNum(x: number) {
    if (x <= 0) { return 1; }
    const pow = Math.pow(10, Math.floor(Math.log10(x)));
    const f = x / pow;
    const nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    return nf * pow;
  }
  get step() { const m = this.maxVal; return m <= 0 ? 1 : this.niceNum(m / 4); }
  get niceMax() { const s = this.step; const m = Math.ceil(this.maxVal / s) * s; return m <= 0 ? s * 4 : m; }
  yFor(v: number) { return this.padT + this.plotH * (1 - v / this.niceMax); }
  fmt(v: number) { return this.prefix + v.toFixed(2) + (this.suffix ? ' ' + this.suffix : ''); }
  tickLabel(v: number) {
    const suf = this.suffix ? ' ' + this.suffix : '';
    if (v >= 1000) { return this.prefix + (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + 'k' + suf; }
    return this.prefix + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)) + suf;
  }

  get ticks() {
    const out: { y: number; label: string }[] = [];
    const nm = this.niceMax, s = this.step;
    for (let v = 0; v <= nm + 1e-9; v += s) { out.push({ y: this.yFor(v), label: this.tickLabel(v) }); }
    return out;
  }
  get zeroY() { return this.yFor(0); }
  get groupW() { return this.plotW / Math.max(1, this.categories.length); }

  private barPath(x: number, y: number, w: number, h: number) {
    const r = Math.min(w / 2, 5, h);
    return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
  }
  get bars() {
    const out: { path: string; gi: number }[] = [];
    const n = this.series.length || 1;
    const gw = this.groupW, inner = gw * 0.68, barW = inner / n, zeroY = this.zeroY;
    this.categories.forEach((c, i) => {
      const center = this.padL + gw * (i + 0.5), left = center - inner / 2;
      this.series.forEach((s, j) => {
        const v = (s.values && s.values[i]) || 0;
        if (v <= 0) { return; }
        const y = this.yFor(v), x = left + j * barW + barW * 0.12, w = barW * 0.76, h = Math.max(1, zeroY - y);
        out.push({ path: this.barPath(x, y, w, h), gi: j });
      });
    });
    return out;
  }
  get xLabels() {
    const gw = this.groupW, n = this.categories.length, k = Math.max(1, Math.ceil(n / 8));
    const out: { x: number; label: string }[] = [];
    this.categories.forEach((c, i) => {
      if (i % k === 0 || i === n - 1) { out.push({ x: this.padL + gw * (i + 0.5), label: c }); }
    });
    return out;
  }

  // ---- interactivity ----
  get bands() {
    const gw = this.groupW;
    return this.categories.map((c, i) => ({ i, x: this.padL + gw * i, w: gw }));
  }
  get highlight() {
    if (this.hoverIndex == null) { return null; }
    const gw = this.groupW;
    return { x: this.padL + gw * this.hoverIndex, w: gw };
  }
  get tipRows() {
    if (this.hoverIndex == null) { return []; }
    const i = this.hoverIndex;
    return this.series.map(s => ({ label: s.label, color: s.color, value: this.fmt((s.values && s.values[i]) || 0) }));
  }
  onEnter(i: number, e: MouseEvent) { this.hoverIndex = i; this.onMove(e); }
  onMove(e: MouseEvent) {
    const el = this.wrap && this.wrap.nativeElement;
    if (!el) { return; }
    const r = el.getBoundingClientRect();
    this.tipX = e.clientX - r.left;
    this.tipY = e.clientY - r.top;
  }
  onLeave() { this.hoverIndex = null; }
}
